import { z } from 'zod';
import { CaregiverStatusSchema, GenderSchema } from './application.schema.js';
import { ArtifactReviewStatusSchema, CredentialTypeSchema } from './credential.schema.js';
import { ComplianceArtifactTypeSchema } from './compliance.schema.js';
import { AdminCaregiverCertificateDetailSchema } from './certificate.schema.js';
import { CanadianPostalCodeSchema, CanadianProvinceCodeSchema, MaxTravelKmSchema } from './profile.schema.js';
// --------- list ---------
/** Phase 14b-2-4 — banking-verification filter derived from the Phase
 *  11b-1 EFT lifecycle columns. `pending` = submitted+!verified+!rejected;
 *  `verified` = eftVerifiedAt set; `rejected` = eftRejectedAt set & not
 *  later verified; `none` = no banking submitted yet. */
export const AdminCaregiverBankingVerificationSchema = z.enum(['pending', 'verified', 'rejected', 'none']);
/** Role-code filter — must track the `role` table (DSW + COOK added Phase 17; add new codes here or the filter 400s). */
export const AdminCaregiverRoleFilterSchema = z.enum(['PSW', 'RPN', 'RN', 'COMPANION', 'DSW', 'COOK']);
/** GET /api/v1/admin/caregivers — query filters + pagination. */
export const AdminCaregiverListQuerySchema = z.object({
    status: CaregiverStatusSchema.optional(),
    /** Phase 14b-2-4 — role-code filter (PSW/RPN/RN/COMPANION/DSW/COOK). */
    role: AdminCaregiverRoleFilterSchema.optional(),
    /** Phase 14b-2-4 — banking-verification filter. */
    bankingVerification: AdminCaregiverBankingVerificationSchema.optional(),
    /** Phase 14d-2 — surface caregivers with any PENDING compliance
     *  artifact (TB / vax / GL insurance / WSIB). Drives the "Pending
     *  compliance docs" section of /admin/vetting. NOT using
     *  `z.coerce.boolean()` because `Boolean('false')` is true — the
     *  explicit string-transform here correctly returns false. */
    hasPendingCompliance: z
        .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
        .optional(),
    /** 15b-6 — "Expiring / expired compliance" vetting section: APPROVED expiring
     *  ≤30d (incl. past-expiry-not-yet-flipped) OR already-EXPIRED. */
    hasExpiringCompliance: z
        .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
        .optional(),
    /** Audit H7 — expiring/expired gating credentials (supersede-aware); a lapse silently drops the caregiver from matching. */
    hasExpiringCredential: z
        .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
        .optional(),
    /** 1-based page index. Offset pagination is fine at v1 scale (~100
     *  caregivers); upgrade to cursor-based when shifts arrive in Phase 9
     *  and listings grow.
     *
     *  Phase 14e-1c — pageSize.max bumped 100→250 so the manual-target
     *  caregiver picker on `/admin/shift-requests/$id` can fetch ~all
     *  ACTIVE caregivers in one page and sort client-side by distance
     *  using `Caregiver.serviceAreaLat/Lng/maxTravelKm`. Server-side
     *  distance-sort + fuzzy text search deferred to v1.x. */
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(250).default(20),
});
/** Single row in the admin list — a denormalised summary view (no
 *  credentials / compliance artifacts; those load on detail). */
export const AdminCaregiverListItemSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    legalGivenName: z.string(),
    legalSurname: z.string(),
    preferredName: z.string().nullable(),
    status: CaregiverStatusSchema,
    declaredRoleCodes: z.array(z.string()),
    /** Phase 14b-2-5 — derived banking-verification state. */
    bankingVerification: AdminCaregiverBankingVerificationSchema,
    applicationSubmittedAt: z.string().datetime().nullable(),
    activatedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /** Phase 14e-1c — geographic matching profile for the manual-target
     *  picker on `/admin/shift-requests/$id` (client-side haversine sort).
     *  Mirrors `Caregiver.serviceArea*` + `maxTravelKm` from Phase 9b;
     *  lat/lng serialised as precision-preserving `string`. NULL lat/lng
     *  (`geo_missing`) is expected for newly-activated caregivers awaiting
     *  operator geocode-retry — the picker shows "no service-area set". */
    serviceAreaLat: z.string().nullable(),
    serviceAreaLng: z.string().nullable(),
    maxTravelKm: z.number().int().positive(),
    serviceAreaGeocodedAt: z.string().datetime().nullable(),
});
export const AdminCaregiverListResponseSchema = z.object({
    items: z.array(AdminCaregiverListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
});
// --------- detail ---------
/** Single credential / compliance artifact as seen by the operator —
 *  same shape as the caregiver-facing view, but with a signed download
 *  URL so the operator can actually view the uploaded scan. */
export const AdminCredentialDetailSchema = z.object({
    id: z.string().uuid(),
    type: CredentialTypeSchema,
    status: ArtifactReviewStatusSchema,
    externalReferenceNumber: z.string().nullable(),
    issuedAt: z.string().date().nullable(),
    expiresAt: z.string().date().nullable(),
    uploadedAt: z.string().datetime().nullable(),
    notes: z.string().nullable(),
    /** Short-TTL signed GET URL — null if no file uploaded yet. */
    downloadUrl: z.string().url().nullable(),
    downloadUrlExpiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const AdminComplianceArtifactDetailSchema = z.object({
    id: z.string().uuid(),
    type: ComplianceArtifactTypeSchema,
    status: ArtifactReviewStatusSchema,
    externalReferenceNumber: z.string().nullable(),
    issuedAt: z.string().date().nullable(),
    expiresAt: z.string().date().nullable(),
    uploadedAt: z.string().datetime().nullable(),
    notes: z.string().nullable(),
    downloadUrl: z.string().url().nullable(),
    downloadUrlExpiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
/** GET /api/v1/admin/caregivers/:id — full operator view. Includes
 *  operator_notes (not visible to the caregiver themselves) and signed
 *  download URLs for every uploaded credential + compliance artifact. */
export const AdminCaregiverDetailResponseSchema = z.object({
    id: z.string().uuid(),
    /** The linked User.id — keys the agreement-acceptance viewer (Phase 23),
     *  distinct from `id` which is the Caregiver row id. */
    userId: z.string().uuid(),
    email: z.string().email(),
    legalGivenName: z.string(),
    legalSurname: z.string(),
    legalMiddleNames: z.string().nullable(),
    preferredName: z.string().nullable(),
    dateOfBirth: z.string().date().nullable(),
    gender: GenderSchema.nullable(),
    phoneMobile: z.string().nullable(),
    phoneAlternate: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    province: z.string().nullable(),
    postalCode: z.string().nullable(),
    selfDescription: z.string().nullable(),
    operatorNotes: z.string().nullable(),
    // Optional "Referred by" free-text captured at apply time (read-only here).
    referralSource: z.string().nullable(),
    // Tax identity — booleans only; SIN never returned (on-file flag), BN shown in full.
    taxEntityType: z.enum(['INDIVIDUAL', 'CORPORATION']),
    sinOnFile: z.boolean(),
    businessNumber: z.string().nullable(),
    eftOnFile: z.boolean(),
    // Bug #6 (Phase 17) — banking verification lifecycle so the operator card can
    // reflect state instead of always offering "Verify". `eftRejected` is the
    // derived "currently rejected, not re-submitted since" state.
    eftVerified: z.boolean(),
    eftRejected: z.boolean(),
    eftRejectionReason: z.string().nullable(),
    /** Last-4 masked account (`••••1234`); full account + void-cheque URL (H4) stay behind /admin/caregivers/:id/banking. */
    bankingAccountLast4: z.string().nullable(),
    hstRegistered: z.boolean(),
    hstNumber: z.string().nullable(),
    // Lifecycle
    status: CaregiverStatusSchema,
    applicationSubmittedAt: z.string().datetime().nullable(),
    activatedAt: z.string().datetime().nullable(),
    suspendedAt: z.string().datetime().nullable(),
    suspensionReason: z.string().nullable(),
    terminatedAt: z.string().datetime().nullable(),
    // Roles / skills
    roles: z.array(z.object({
        code: z.string(),
        payRateOverride: z.number().nullable(),
    })),
    skills: z.array(z.string()),
    immunizations: z.array(z.string()), // voluntary declaration (2026-08 vaccine revamp)
    // Vetting artifacts
    credentials: z.array(AdminCredentialDetailSchema),
    complianceArtifacts: z.array(AdminComplianceArtifactDetailSchema),
    additionalCertificates: z.array(AdminCaregiverCertificateDetailSchema), // doc 16 — optional, unverified
    // Phase 14e-1c — matching geo profile (mirrors AdminCaregiverListItem)
    serviceAreaLat: z.string().nullable(),
    serviceAreaLng: z.string().nullable(),
    maxTravelKm: z.number().int().positive(),
    serviceAreaGeocodedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// --------- review ---------
/** Approve a credential / compliance artifact. Notes are optional but
 *  useful when the operator wants to record context for future ops. */
export const AdminApproveRequestSchema = z.object({
    notes: z.string().max(2000).trim().nullable().optional(),
});
/** Reject a credential / compliance artifact. Notes are REQUIRED so the
 *  caregiver gets actionable feedback ("scan is blurry, please re-upload"). */
export const AdminRejectRequestSchema = z.object({
    notes: z.string().min(1).max(2000).trim(),
});
// Lifecycle-transition schemas (activate / suspend / unsuspend / terminate)
// extracted to caregiver/lifecycle-admin.schema.ts per Convention 06's
// extraction-as-trigger rule (Phase 14e-1c — keep this file ≤250 LOC after
// the geo field additions).
// --------- patch (operator edits) ---------
/** Operator-permitted edits — separate from the caregiver-facing patch.
 *  Includes things the caregiver CANNOT edit themselves: legal name
 *  corrections (after the operator verifies ID), operator-only notes. */
export const AdminCaregiverPatchSchema = z.object({
    legalGivenName: z.string().min(1).max(120).trim().optional(),
    legalSurname: z.string().min(1).max(120).trim().optional(),
    legalMiddleNames: z.string().max(240).trim().nullable().optional(),
    preferredName: z.string().min(1).max(120).trim().nullable().optional(),
    dateOfBirth: z.string().date().nullable().optional(),
    gender: GenderSchema.nullable().optional(), // operator can set/correct
    phoneMobile: z.string().min(10).max(30).trim().nullable().optional(),
    phoneAlternate: z.string().min(10).max(30).trim().nullable().optional(),
    // Phase 5f-d audit r3 — operator can fix address fields on a caregiver whose
    // self-edit is locked (UNDER_REVIEW), else the address_incomplete activation
    // gate deadlocks. Mirrors the self-patch address shape; geocoded at the handler.
    addressLine1: z.string().max(200).trim().nullable().optional(),
    addressLine2: z.string().max(200).trim().nullable().optional(),
    city: z.string().max(120).trim().nullable().optional(),
    province: CanadianProvinceCodeSchema.nullable().optional(),
    postalCode: CanadianPostalCodeSchema.nullable().optional(),
    operatorNotes: z.string().max(10_000).trim().nullable().optional(),
    // Backstop for the caregiver-declared travel radius (same 1–150 bound).
    maxTravelKm: MaxTravelKmSchema.optional(),
});
//# sourceMappingURL=admin.schema.js.map